<template>
    <section class="relative overflow-hidden bg-lifeboost-gray">
      <!-- Background Image -->
      <nuxt-img 
        :src="backgroundImage" 
        alt="Hero Background" 
        preload loading="eager" fetchpriority="high"
        class="absolute inset-0 w-full object-cover h-full md-max:object-[5%_100%] lg:object-top" 
        format="webp"
      />
  
      <!-- Content Container -->
      <div class="relative z-10 pb-10 mx-auto max-w-screen-2xl lg:px-12 3xl:px-4 lg:py-12 monitor-only:px-4 xl:px-16 lg:pt-8 sm:pb-8 md:pb-24">
        <div class="grid grid-cols-12 lg:gap-x-12">
          <!-- Text Content -->
          <div class="grid items-center order-1 col-span-12 px-4 lg:col-span-6 lg:order-2 lg:px-0 md:px-10">
            <div>
              <!-- Hero Title -->
              <h1 class="mt-6 font-bold text-center text-white lg:text-48 text-53 2xl:text-51 leading-52 lg:mt-0 lg:text-left font-ww-medium" v-html="heroTitle">
             
              </h1>
  
              <!-- Hero Subtitle -->
              <h2 class="mt-4 text-center text-white lg:text-left 2xl:text-27 lg:text-25 text-27 leading-32 font-ww-medium" v-html="heroSubtitle">
              
              </h2>
  
              <!-- Button -->
              <div class="flex flex-col justify-start mt-8 space-y-6 lg:space-y-8 lg:items-start md:mt-8">
                <div class="text-center lg:px-0 sm:px-4">
                  <button @click="scrollTo(buttonLink)" class="flex items-center w-full bnt-icon-hero md:w-auto !bg-[#9e3736]">
                    <span class="flex items-center">{{ buttonText }}</span>
                    <svg class="relative ml-4 top-0.5 sm:w-8 w-8" xmlns="http://www.w3.org/2000/svg" width="38px" height="31px" viewBox="0 0 38 31">
                      <path fill-rule="evenodd" fill="rgb(255, 255, 255)" d="M37.423,17.013 L24.160,30.420 C23.389,31.199 22.139,31.200 21.369,30.422 C20.599,29.643 20.600,28.381 21.371,27.601 L31.183,17.683 L1.631,17.683 C0.730,17.683 -0.001,16.945 -0.001,16.033 L-0.001,15.338 C-0.001,14.426 0.730,13.688 1.631,13.688 L31.541,13.688 L21.362,3.398 C20.592,2.619 20.593,1.357 21.364,0.577 C22.135,-0.202 23.384,-0.203 24.154,0.575 L37.401,13.965 C37.743,14.311 37.924,14.754 37.962,15.206 C38.088,15.839 37.909,16.522 37.423,17.013 Z"/>
                    </svg>
                  </button>
                </div>
  
                <!-- Rating and Statistics -->
                <div class="items-center justify-center block lg:mt-0">
                  <div class="flex flex-col items-center justify-center md:flex-row lg:justify-start lg:block md:gap-y-0 gap-y-3">
                    <div class="float-left p-1 pr-4">
                      <nuxt-img :src="ratingImage" alt="Rating Stars" width="160" height="23" class="mx-auto sm:w-32 w-36 lg:w-40" format="webp" />
                    </div>
  
                    <div class="float-left mx-auto text-2xl font-bold text-center text-white md:contents lg:mt-0 sm:text-22 font-ww-regular">
                      {{ statsText }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Secondary Image -->
          <div class="flex items-center col-span-12 lg:col-span-6 lg:order-2 min-h-96">
            <nuxt-img :src="secondaryImage" alt="Hero Image" sizes="xs:260px sm:288px md:384px lg:460px xl:460px" preload loading="eager" width="500" height="688" fetchpriority="high" class="mx-auto md:max-w-[460px] max-w-96" format="webp" />
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  
  const props = defineProps({
    backgroundImage: {
      type: String,
      default: "/assets/images/patriot-bg-crop.png"
    },
    heroTitle: {
      type: String,
      default: "Raise a Mug to Freedom!"
    },
    heroSubtitle: {
      type: String,
      default: "Discover the Best Single-Origin, Low Acid Coffee Beans In Our New Patriot Specialty Coffee! Act Now - there’s A VERY Limited Supply!"
    },
    buttonText: {
      type: String,
      default: "Try it now!"
    },
    buttonLink: {
      type: String,
      default: "#"
    },
    secondaryImage: {
      type: String,
      default: "/assets/images/HeaderImageBagcoffee.png"
    },
    statsText: {
      type: String,
      default: "Over 60,000,000 Cups of Lifeboost Coffee Served"
    },
    ratingImage: {
      type: String,
      default: "/assets/images/Group-36.png"
    }
  });
  
  
  // Define Builder component
  defineBuilderComponent({
    friendlyName: 'Hero Background And Image',
    inputs: [
      {
        name: 'backgroundImage',
        type: 'file',
        allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
        defaultValue: '/assets/images/patriot-bg-crop.png'
      },
      {
        name: 'heroTitle',
        type: 'richtext',
        defaultValue: 'Raise a Mug to Freedom!'
      },
      {
        name: 'heroSubtitle',
        type: 'richtext',
        defaultValue: 'Discover the Best Single-Origin, Low Acid Coffee Beans In Our New Patriot Specialty Coffee! Act Now - there’s A VERY Limited Supply!'
      },
      {
        name: 'buttonText',
        type: 'text',
        defaultValue: 'Try it now!'
      },
      {
        name: 'buttonLink',
        type: 'text',
        defaultValue: '#'
      },
      {
        name: 'secondaryImage',
        type: 'file',
        allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
        defaultValue: '/assets/images/HeaderImageBagcoffee.png'
      },
      {
        name: 'statsText',
        type: 'text',
        defaultValue: 'Over 60,000,000 Cups of Lifeboost Coffee Served'
      },
      {
        name: 'ratingImage',
        type: 'file',
        allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
        defaultValue: '/assets/images/Group-36.png'
      }
    ]
  });
  </script>
  